import { useNavigate, useSearch } from '@tanstack/react-router'
import { AppRoute } from 'appRoutes'
import { useDefaultPredefinedRanges } from 'features/commonUI'
import { PredefinedRange } from 'helpers/dateRange'
import { DateTime } from 'luxon'
import { DateRange, DateRangePeriod, VisitsFilter, VisitsFilterAndDateRange, VisitsFilterKeysWithoutDate } from 'models'
import { ampli } from 'models/ampli'
import { useMemo } from 'react'

import { BotDetectionUrlKey, IdentificationUrlKeys, Platform, SmartSignalUrlKey, VisitsFilterView } from '../types'
import {
  allSmartSignalFilterSet,
  botDetectionFilterSet,
  smartSignalFilterPlatformMap,
  visitFilterLabelMap,
} from '../utils/const'

export const EVENTS_FILTER_PARAMS: Array<keyof VisitsFilterAndDateRange> = [
  IdentificationUrlKeys.RequestId,
  IdentificationUrlKeys.VisitorId,
  IdentificationUrlKeys.ProximityId,
  IdentificationUrlKeys.IpAddress,
  IdentificationUrlKeys.Url,
  IdentificationUrlKeys.LinkedId,
  IdentificationUrlKeys.Origin,
  IdentificationUrlKeys.Asn,
  IdentificationUrlKeys.Since,
  IdentificationUrlKeys.Before,
  IdentificationUrlKeys.Period,
  IdentificationUrlKeys.Environment,
  IdentificationUrlKeys.SdkPlatform,
  IdentificationUrlKeys.SdkVersion,
  IdentificationUrlKeys.Limit,
  IdentificationUrlKeys.MinSuspectScore,
  IdentificationUrlKeys.Bot,
  IdentificationUrlKeys.BotName,
  IdentificationUrlKeys.BotCategory,
  IdentificationUrlKeys.BotProvider,
  IdentificationUrlKeys.BotIdentity,
  IdentificationUrlKeys.BotConfidence,
  IdentificationUrlKeys.Vpn,
  IdentificationUrlKeys.IpBlocklist,
  IdentificationUrlKeys.ResidentialProxy,
  IdentificationUrlKeys.Incognito,
  IdentificationUrlKeys.BrowserTampering,
  IdentificationUrlKeys.VirtualMachine,
  IdentificationUrlKeys.PrivacySettings,
  IdentificationUrlKeys.DeveloperTools,
  IdentificationUrlKeys.Emulator,
  IdentificationUrlKeys.Simulator,
  IdentificationUrlKeys.ClonedApp,
  IdentificationUrlKeys.LocationSpoofing,
  IdentificationUrlKeys.MitmAttack,
  IdentificationUrlKeys.DeviceRarity,
  IdentificationUrlKeys.ActiveCall,
  IdentificationUrlKeys.RootedDevice,
  IdentificationUrlKeys.JailbrokenDevice,
  IdentificationUrlKeys.Frida,
  IdentificationUrlKeys.Tor,
  IdentificationUrlKeys.HighRecallId,
]
const MANAGED_SET = new Set(EVENTS_FILTER_PARAMS)
const identificationEventsRouteId = '/_authenticated/_layout/workspaces/$subscriptionId/events/' as const
const identificationEventsPath = `${AppRoute.IdentificationEvents}/` as const

function fireAnalyticsEventForSmartSignalFilters(params: Partial<VisitsFilterView>) {
  const paramKeys = (Object.keys(params) as Array<keyof VisitsFilterView>).filter((key) => params[key] !== undefined)

  paramKeys.forEach((paramKey) => {
    if (paramKey && allSmartSignalFilterSet.has(paramKey as SmartSignalUrlKey)) {
      let platformType: Platform.Web | Platform.Mobile | Platform.Universal = Platform.Universal

      for (const [platform, signals] of smartSignalFilterPlatformMap.entries()) {
        if (signals.includes(paramKey as SmartSignalUrlKey)) {
          platformType = platform
          break
        }
      }

      ampli.smartSignalFilterApplied({
        datasetFocus: 'Identification Events',
        'App Page Title': document.title,
        'App Page Path': AppRoute.IdentificationEvents,
        platformType,
        smartSignal: visitFilterLabelMap[paramKey as VisitsFilterKeysWithoutDate] || paramKey,
      })
    } else if (paramKey && botDetectionFilterSet.has(paramKey as BotDetectionUrlKey)) {
      ampli.smartSignalFilterApplied({
        datasetFocus: 'Identification Events',
        'App Page Title': document.title,
        'App Page Path': AppRoute.IdentificationEvents,
        platformType: Platform.Universal,
        smartSignal: visitFilterLabelMap[paramKey as VisitsFilterKeysWithoutDate] || paramKey,
      })
    }
  })
}

export const useFilterSearchParams = () => {
  const currentSearchParams = useSearch({ from: identificationEventsRouteId })
  const navigate = useNavigate({ from: identificationEventsPath })
  const { ranges } = useDefaultPredefinedRanges()

  const handleSetSearchParams = (paramsToBeSet: Partial<VisitsFilterView>) => {
    const managedChanges = Object.fromEntries(
      Object.entries(paramsToBeSet).filter(([key]: [keyof VisitsFilterView, any]) => MANAGED_SET.has(key))
    )

    fireAnalyticsEventForSmartSignalFilters(paramsToBeSet)

    navigate({ search: (old) => ({ ...old, ...managedChanges }) })
  }

  const resetSearchParams = (paramsToBeRemoved: Array<keyof VisitsFilterView>) => {
    navigate({
      search: (old) => ({
        ...old,
        ...Object.fromEntries(paramsToBeRemoved.map((key) => [key, undefined])),
      }),
    })
  }

  const adaptedDate = dateRangeAdapter({
    ...(currentSearchParams.period ? ({ period: currentSearchParams.period } as DateRangePeriod) : {}),
    before: currentSearchParams.before,
    since: currentSearchParams.since,
    ranges,
  })

  const dateRange = useMemo(
    () => ({
      ...(adaptedDate.period ? ({ period: adaptedDate.period } as DateRangePeriod) : {}),
      startDate: adaptedDate.since,
      endDate: adaptedDate.before,
    }),
    [adaptedDate]
  )

  const filterValues = useMemo(() => filterAdapter(currentSearchParams), [currentSearchParams])
  const adaptedFilterValues = useMemo(
    () => visitsFilterAdapter(currentSearchParams as any, ranges),
    [currentSearchParams, ranges]
  )

  return {
    filterValues,
    adaptedFilterValues,
    dateRange,
    setSearchParams: handleSetSearchParams,
    resetSearchParams,
  }
}

// Add the CIDR notation if it's missing.
function ipAddressAdapter(ipAddress?: string) {
  if (ipAddress === undefined) {
    return {}
  }

  if (ipAddress === '' || ipAddress.includes('/')) {
    return { ipAddress }
  }

  const isIpV4 = ipAddress.includes('.')
  const postFix = isIpV4 ? 32 : 128

  return {
    ipAddress: `${ipAddress}/${postFix}`,
  }
}

function dateRangeAdapter({
  period,
  before,
  since,
  ranges,
}: {
  period?: DateRangePeriod['period']
  before?: string
  since?: string
  ranges: Record<DateRangePeriod['period'], PredefinedRange>
}): DateRange & DateRangePeriod {
  let periodTransformed = {} as DateRangePeriod
  let sinceTransformed = {}
  let beforeTransformed = {}

  if (period && ranges[period]) {
    sinceTransformed = {
      since: ranges[period].startDate,
    }

    beforeTransformed = {
      period,
      before: ranges[period].endDate.endOf('day'),
    }
  } else {
    if (since) {
      sinceTransformed = {
        since: DateTime.fromISO(since),
      }
    }
    if (before) {
      beforeTransformed = {
        before: DateTime.fromISO(before),
      }
    }
  }

  const isBeforeValid = (beforeTransformed as { before: DateTime })?.before?.isValid
  const isSinceValid = (sinceTransformed as { since: DateTime })?.since?.isValid

  if ((!period && !since && !before) || !isBeforeValid || !isSinceValid) {
    periodTransformed = {
      period: 'today',
      since: DateTime.fromJSDate(new Date()).startOf('day'),
      before: DateTime.fromJSDate(new Date()).endOf('day'),
    } as DateRange & DateRangePeriod
  }

  return {
    ...sinceTransformed,
    ...beforeTransformed,
    ...periodTransformed,
  } as DateRange & DateRangePeriod
}

type FiltersFromUrl = VisitsFilter & {
  before?: string
  since?: string
} & Partial<DateRangePeriod>
type FiltersWithoutAdapters = Omit<VisitsFilterAndDateRange, 'ipAddress' | 'period' | 'before' | 'since'>

function filterAdapter(params: object): Partial<VisitsFilterView> {
  return Object.fromEntries(
    Object.entries(params).filter(
      ([key, value]) => value !== undefined && MANAGED_SET.has(key as keyof VisitsFilterView)
    )
  ) as Partial<VisitsFilterView>
}

function visitsFilterAdapter(
  { ipAddress, period, before, since, ...params }: FiltersFromUrl,
  ranges: Record<DateRangePeriod['period'], PredefinedRange>
): VisitsFilter & DateRangePeriod & DateRange {
  const onlyManagedParams = Object.fromEntries(
    Object.keys(params)
      .filter((p) => EVENTS_FILTER_PARAMS.includes(p as keyof VisitsFilterAndDateRange))
      .map((param) => [param, params[param as keyof FiltersWithoutAdapters]])
  ) as Partial<FiltersWithoutAdapters>

  return {
    ...onlyManagedParams,
    ...ipAddressAdapter(ipAddress),
    ...dateRangeAdapter({ period, before, since, ranges }),
  }
}
